<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ user ? $t('edit_user') : $t('invite_new_user') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close

      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        div.text-md.font-semibold.neutral700--text.mb-4(v-if="!user") {{ $t('fill_the_information_below') }}

        .form-input
          v-form(ref="form")
            div.text-sm.font-semibold.neutral500--text {{ $t('email') }}
            v-text-field.text-sm.mt-2.round-xl(v-model="email" :placeholder="$t('enter_email')" :rules="rules.email" outlined hide-details="auto")
            div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('role') }}
            v-radio-group(v-model="role" hide-details :rules="rules.not_empty")
              div.d-flex.align-start
                v-radio.mb-0(value="1" color="basic700" )
                div(@click="role = '1'")
                  div.text-md.font-normal.basic700--text {{ $t('admin') }}
                  div.text-sm.font-normal.basic500--text {{ $t('admin_desc') }}
              div.d-flex.align-start.mt-3
                v-radio.mb-0(value="2" color="basic700" )
                div(@click="role = '2'")
                  div.text-md.font-normal.basic700--text {{ $t('clinic_assistant') }}
                  div.text-sm.font-normal.basic500--text {{ $t('clinic_assistant_desc') }}

      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0(@click="closeDialog" text) {{ $t('cancel') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed dark @click="saveData") {{ user ? $t('save') : $t('invite') }}

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  name: 'EditUserDialog',
  props: ['open', 'user'],
  data () {
    return {
      isLoading: false,
      email: null,
      role: null,
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required'],
        email: [v => !!v || 'E-mail address is required', v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid'],
      },
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    }
  },
  mounted () {
    if (this.user) {
      this.setData()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.email = this.user.user.email
      this.role = this.user.role === 'Admin' ? '1' : '2'
    },
    saveData () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      if (this.user) {
        const data = {
          role: this.role,
          user: {
            email: this.email
          }
        }
        this.$axios.patch(`/members/${this.user.id}`, data)
          .then(() => {
            this.closeResetDialog()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        const data = {
          company: this.company.id,
          email: this.email,
          role: this.role,
          inviter: this.member.id,
          app: 'clinic'
        }
        this.$axios.post(`/invitations/`, data)
          .then((response) => {
            this.sendEmailInvitation(response.data.id)
          })
          .catch(() => {
            this.$axios.get(`/invitations/?company=${this.company.id}&accepted=false&email=${this.email}`)
              .then((response) => {
                if (response.data.results.length != 0) {
                  this.sendEmailInvitation(response.data.results[0].id)
                } else {
                  this.showMessage(['error', this.$t('error_message.user_accepted_invitation')])
                  this.isLoading = false
                }
              })
              .catch(() => {
                this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
                this.isLoading = false
              })
          })
      }
    },
    sendEmailInvitation(id) {
      this.$axios.post(`/invitations/${id}/send/?company=${this.company.id}`)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.invitation_sent')])
          this.closeResetDialog()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    closeDialog () {
      this.showDialog = false
      this.$emit('closed')
    },
    closeResetDialog () {
      this.showDialog = false
      this.$emit('reset')
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>